import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import usePrices from './usePrices';
import debounce from 'lodash/debounce';
import useNavigate from './useNavigate';

export default function useEcommerceCheckout() {
  const store = useStore();
  const { convertNumberInBRLPrice } = usePrices();
  const { getRouteParam, getQueryParam, addQueryParams, removeQueryParam } = useNavigate();

  const offerSlug = ref(getQueryParam('bump-offer') || getRouteParam('slug'));
  const offer = computed(() => store.getters['offer/getOffer'](offerSlug.value)?.offer);
  const offerUpsell = computed(() => offer.value?.offerUpsells || []);
  const offerDowngrade = computed(() => offer.value?.offerDowngrade);

  const cart = computed(() => store.getters['cart/getCart']);
  const isSubmittingCart = computed(() => store.getters['cart/getIsSubmittingCart']);

  const mainOfferSlug = ref(getRouteParam('slug'));
  const mainOffer = computed(() => store.getters['offer/getOffer'](mainOfferSlug.value));

  const bumpOfferSelected = ref(getQueryParam('bump-offer'));
  const mainOfferBumpOffers = computed(() => {
    const offerBumps = mainOffer.value?.offer?.offerBumps || [];

    if (offerBumps.length && bumpOfferSelected.value) {
      return offerBumps.map((offer) => {
        return {
          ...offer,
          checked: bumpOfferSelected.value === offerBumps[2]?.offerSlug || offer.offerSlug === bumpOfferSelected.value,
        };
      });
    }

    return offerBumps;
  });
  const offerBumpsDisplay = computed(() => {
    if (!mainOfferBumpOffers.value.length) {
      return [];
    }

    if (mainOfferBumpOffers.value.length === 3) {
      return mainOfferBumpOffers.value.slice(0, -1);
    }

    return mainOfferBumpOffers.value.slice(0, 1);
  });

  const priceByInstallment = computed(() => store.getters['cart/getPriceByInstallment']);
  const priceByInstallmentSelected = computed(() => store.getters['cart/getCartInstallments']);
  const priceByInstallmentFormatted = computed(() => {
    return priceByInstallment.value.map((installment) => {
      return {
        value: installment.installment,
        text: `(${installment.installment}x ${convertNumberInBRLPrice(installment.price, true)} s/juros)`,
      };
    });
  });
  const priceByInstallmentFormattedValues = computed(() => {
    let valueFinal = priceByInstallment.value.map((installment) => {
      return {
        value: installment.installment,
        text: `${convertNumberInBRLPrice(installment.price, true)}`,
      };
    });
    return valueFinal.find((price) => price.value === priceByInstallmentSelected.value).text;
  });
  const priceByInstallmentFormattedWithoutInterest = computed(() => {
    return priceByInstallment.value.map((installment) => {
      return {
        value: installment.installment,
        text: `(${installment.installment}x ${convertNumberInBRLPrice(installment.price, true)})`,
      };
    });
  });

  const upsertCartDebounce = debounce((oldCartID) => {
    store.dispatch('cart/offerCart', { offerSlug: offerSlug.value, oldCartID: oldCartID });
  }, 1000);

  const initialReset = (couponCode = '') => {
    store.commit('offer/SET_CURRENT_OFFER_SLUG', { offerSlug: offerSlug.value });
    store.dispatch('cart/cartReset', { pmp: getQueryParam('pmp'), couponCode: couponCode });
  };

  const preFetchOffer = async (offerSlug = '') => {
    if (!offerSlug) return;

    await store.dispatch('offer/offerRead', { offerSlug: offerSlug });
  };

  const onToggleOrderBump = async ({ slug }) => {
    store.commit('cart/SET_IS_SUBMITTING_CART', { isSubmittingCart: true });

    const upsertSlug =
      slug.length === 2 && mainOfferBumpOffers.value.length === 3 ? mainOfferBumpOffers.value[2].offerSlug : slug[0];

    offerSlug.value = upsertSlug || mainOfferSlug.value;
    bumpOfferSelected.value = upsertSlug || '';

    if (upsertSlug) {
      await addQueryParams({ 'bump-offer': bumpOfferSelected.value });
    } else {
      await removeQueryParam('bump-offer');
    }

    upsertCartDebounce(cart.value.cartID);
  };

  const handleOfferSlug = (slug) => {
    offerSlug.value = slug;
  };

  return {
    cart: cart,
    offer: offer,
    initialReset,
    preFetchOffer,
    handleOfferSlug,
    onToggleOrderBump,
    upsertCartDebounce,
    offerSlug: offerSlug,
    offerUpsell: offerUpsell,
    mainOfferSlug: mainOfferSlug,
    offerDowngrade: offerDowngrade,
    isSubmittingCart: isSubmittingCart,
    offerBumpsDisplay: offerBumpsDisplay,
    priceByInstallmentSelected: priceByInstallmentSelected,
    priceByInstallmentFormatted: priceByInstallmentFormatted,
    priceByInstallmentFormattedValues: priceByInstallmentFormattedValues,
    priceByInstallmentFormattedWithoutInterest,
  };
}
